import { Container, Icon, Row, Col, index } from "design-react-kit";
import Link from "./Link";
import React from "react";
import { useTranslate } from "react-admin";
import { footerImages } from "../assets/footer";
import {
  FaXTwitter,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa6";

const SOCIAL_ICONS = [
  { name: "facebook", icon: FaFacebookF },
  { name: "x-twitter", aliases: ["twitter"], icon: FaXTwitter },
  { name: "instagram", icon: FaInstagram },
  { name: "linkedin", icon: FaLinkedinIn },
  { name: "youtube", icon: FaYoutube },
];

const findSocialIcon = (itemName) =>
  SOCIAL_ICONS.find(
    (icon) =>
      icon.name === itemName ||
      (icon.aliases && icon.aliases.includes(itemName)),
  );

const parseStyle = (styleStr) => {
  if (!styleStr) return {};
  return styleStr.split(";").reduce((acc, rule) => {
    const [prop, value] = rule.split(":").map((s) => s.trim());
    if (prop && value) {
      const camelProp = prop.replace(/-([a-z])/g, (_, l) => l.toUpperCase());
      acc[camelProp] = value;
    }
    return acc;
  }, {});
};

const Footer = () => {
  const translate = useTranslate();

  const t = (key) => {
    const result = translate(key, { _: "" });
    return result === "" ? null : result;
  };

  const tList = (key) => {
    const result = t(key);
    if (!result) return [];
    return result
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const sectionNames = tList("website.footer.sections");
  const sectionTitles = tList("website.footer.sections.titles");

  const sections = sectionNames.map((sectionName, index) => {
    const base = `website.footer.${sectionName}`;
    const itemNames = tList(`${base}.items`);

    const items = itemNames.map((itemName) => {
      const socialIcon = findSocialIcon(itemName);
      if (socialIcon) {
        const href = t(`${base}.${itemName}.href`);
        return {
          name: itemName,
          icon: socialIcon.icon,
          text: null,
          href,
          style: {},
          isSocial: !!href,
        };
      }

      const text = t(`${base}.${itemName}.text`);
      const href = t(`${base}.${itemName}.href`);
      const style = parseStyle(t(`${base}.${itemName}.style`));

      return { name: itemName, text, href, style, isSocial: false };
    });

    return {
      name: sectionName,
      title: sectionTitles[index] || null,
      items,
    };
  });

  const smallPrintNames = tList("website.footer.smallprints");
  const smallPrints = smallPrintNames
    .map((name) => ({
      text: t(`website.footer.smallprint.${name}.text`),
      href: t(`website.footer.smallprint.${name}.href`),
      style: parseStyle(t(`website.footer.smallprint.${name}.style`)),
    }))
    .filter((l) => l.text && l.href);

  const renderItem = (item) => {
    if (item.isSocial) {
      return (
        <a
          key={item.name}
          aria-label={item.name.charAt(0).toUpperCase() + item.name.slice(1)}
          href={item.href}
          rel="noopener noreferrer"
          target="_blank"
          className="social-link"
        >
          <item.icon />
        </a>
      );
    }

    if (item.text && item.href) {
      return (
        <p key={item.name} style={item.style}>
          <a href={item.href} className="footer-link" data-focus-mouse="false">
            {item.text}
          </a>
        </p>
      );
    }

    if (item.text) {
      return (
        <p
          key={item.name}
          style={item.style}
          dangerouslySetInnerHTML={{ __html: item.text }}
        />
      );
    }

    return null;
  };

  const isSocialSection = (section) =>
    section.items.length > 0 && section.items.every((i) => i.isSocial);

  const colSize = Math.floor(12 / Math.max(sections.length, 1));

  return (
    <footer className="it-footer" role="contentinfo">
      <div className="it-footer-main">
        <Container>
          <section
            className="py-4 res-footer-blocks"
            aria-label="Sezioni del footer"
          >
            <div className="row">
              {sections.map((section, index) => {
                const isLast = index === sections.length - 1;
                const isFirst = index === 0;
                // Su mobile (col-12) tutte le sezioni sono allineate a sinistra.
                // Da `md` in su distribuiamo: prima a sinistra, ultima a destra,
                // intermedie centrate.
                let alignClass = "";
                if (!isFirst) {
                  alignClass = isLast
                    ? "d-md-flex justify-content-md-end"
                    : "d-md-flex justify-content-md-center";
                }
                return (
                  <div
                    key={section.name}
                    className={`col-12 col-md-4 col-lg-${colSize} pb-2 ${alignClass}`}
                  >
                    <div className="it-socials">
                      {section.title && (
                        <h2 className="footer-heading">{section.title}</h2>
                      )}

                      {isSocialSection(section) ? (
                        <ul className="social-links">
                          {section.items.map((item) => (
                            <li key={item.name}>{renderItem(item)}</li>
                          ))}
                        </ul>
                      ) : (
                        section.items.map((item) => renderItem(item))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </Container>
      </div>

      <div className="it-footer-small-prints clearfix">
        <Container>
          <Row>
            <Col lg={9}>
              <h2 className="sr-only">{t("website.footer.links")}</h2>
              <ul className="it-footer-small-prints-list list-inline mb-0 d-flex flex-column flex-md-row">
                {smallPrints.map((link) => (
                  <li className="list-inline-item" key={link.text}>
                    <Link
                      href={link.href}
                      title={link.text}
                      style={link.style}
                      className="footer-small-link"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </Col>
            <Col lg={3} className="footerImages">
              <img
                className="footer-logo-image"
                src={footerImages[7]}
                alt="Logo Regione Campania"
              />
            </Col>
          </Row>
        </Container>
      </div>
    </footer>
  );
};

export default Footer;
